/* ============================================================================
   M2 — Agreements Overview  (route: /supply/agreements)
   Live-data version — backed by the Courier Setup APIs (see ../api.js):
     page-head  = "Agreements Overview" + Upload Agreement (admin)
     KPI strip  = dashboard().agreements / coverage
     risk tiles = dashboard().risk (server-computed governance numbers)
     table      = parents (listParents) + per-parent rollups (dashboard().perParent)
     expand     = lazy agreementTimeline(parentId) + codesByParent(parentId):
                  parent-wide agreements mini-table + per-code sections with
                  agreement badge, deep-dive link and code-scoped upload
   Upload Agreement drawer (M2.4) — multipart POST .../parent/agreement.
   ========================================================================== */
import React, {useEffect, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {useNavigate, useSearchParams} from 'react-router-dom'
import AddIcon from '@mui/icons-material/Add'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import SearchIcon from '@mui/icons-material/Search'
import WarningAmberIcon from '@mui/icons-material/WarningAmberOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import SettingsInputComponentOutlinedIcon from '@mui/icons-material/SettingsInputComponentOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import SyncIcon from '@mui/icons-material/Sync'
import UploadIcon from '@mui/icons-material/UploadOutlined'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined'
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined'
import CloseIcon from '@mui/icons-material/Close'
import HistoryIcon from '@mui/icons-material/History'
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined'

import {Box, Stack, Typography, Button, Chip} from '@mui/material'
import {alpha} from '@mui/material/styles'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import PageTitle from '../../../components/PageTitle'
import {
  Badge,
  KpiStrip,
  SupplyTable,
  FileDrop,
  Modal,
  ConfirmDialog,
  SideDrawer,
  SearchSelect,
  useToast,
  fmtDate,
  fmtDateTime,
  initials,
  TableSkeleton,
  LoadErrorPanel,
  SupplyHero,
  HERO_BTN_SOLID,
} from '../components'
import {apiError, AGREEMENT_TYPE_ENUM, STAGE_ENUM} from '../supplyMeta'
import {
  useLazyListParentsQuery,
  useLazyDashboardQuery,
  useAgreementTimelineQuery,
  useCodesByParentQuery,
  useLazyCodesByParentQuery,
  useLazyDownloadAgreementQuery,
  useUploadAgreementMutation,
  useApproveAgreementMutation,
  useRejectAgreementMutation,
  useSupersedeAgreementMutation,
} from '../../../redux/supplyApiSlice'
import {getAccess, isAdmin} from '../../../helpers/ReduxHelpers'
import PartnerTimeline from './PartnerTimeline'
import StageDrawer from './StageDrawer'
import {
  agrTypeShort,
  agrTypeFull,
  isOpenStage1,
  isSignedGoverning,
  riskChips,
  codeBadgeFor,
  colorFor,
  codeLabel,
  STAGE1_SLA_DAYS,
} from './agreementHealth'

/* sample template body for the upload drawer (FileDrop sample download) */
const AGR_TEMPLATE = [
  'ProShip — Courier Agreement Upload Template',
  '----------------------------------------------',
  'Partner            : <parent courier name, must exist in Partner Directory>',
  'Freight vertical   : D2C',
  'Agreement type     : Master Agreement (MSA) | Commercial Addendum | Operational Addendum',
  'Addendum number    : <integer, addendums only — blank = system auto-assigns next>',
  'Document title     : <min 5 chars, e.g. Delhivery Rate Revision Q1 FY27 — ENT Surface Codes>',
  'Stage              : Stage 1: Email Received | Stage 2: Agreement Signed',
  'Effective from     : YYYY-MM-DD (date terms apply, NOT the date received)',
  'Effective to       : YYYY-MM-DD or blank for open-ended',
  'Applies to         : All Codes | Specific Codes',
  'Specific codes     : <select the courier account codes if Applies to = Specific Codes>',
  'Internal notes     : <optional, max 500 chars>',
  '',
  'Attach the signed/approved document (PDF, DOCX, PNG, JPG, EML, MSG — max 10 MB).',
].join('\n')

/* module scope: D2C only */
const VISIBLE_VERTICALS = ['D2C']

/* ───────── tiny presentational helpers ───────── */

/* stage badge (amber = Stage 1, green = Stage 2) — server stage enum */
function AgrStageBadge({agr: a}) {
  if (a.stage === 'SIGNED')
    return (
      <span className='mini-pill mp-green' style={{fontSize: 11}}>
        Stage 2 · Signed
      </span>
    )
  return (
    <span className='mini-pill mp-amber' style={{fontSize: 11}}>
      Stage 1 · Pending signing
    </span>
  )
}

/* status summary pills for a parent (from the dashboard per-parent rollup) */
function AgrParentSummary({row: r}) {
  if (!r.signed && !r.pending) return <span className='mini-pill mp-grey'>No agreements</span>
  return (
    <span style={{display: 'inline-flex', flexWrap: 'wrap', gap: 5, verticalAlign: 'top'}}>
      {r.signed ? <span className='mini-pill mp-green'>{r.signed} signed</span> : null}
      {r.pending ? <span className='mini-pill mp-amber'>{r.pending} pending</span> : null}
    </span>
  )
}

/* chips for an agreement's verticals (server: list) */
function AgrVertChips({verticals}) {
  return (
    <>
      {(verticals || []).map((v) => (
        <span key={v} className={'chip chip-' + String(v).toLowerCase()}>
          {v}
        </span>
      ))}
    </>
  )
}

/* code chips (max 3 + overflow) — account codes */
function CodeChips({codes}) {
  codes = codes || []
  if (!codes.length) return <span className='muted'>—</span>
  const max = 3
  const shown = codes.slice(0, max)
  const rest = codes.slice(max)
  return (
    <span style={{display: 'inline-flex', flexWrap: 'wrap', gap: 5, verticalAlign: 'top'}}>
      {shown.map((c) => (
        <span key={c} className='tag tag-mono'>
          {c}
        </span>
      ))}
      {rest.length ? (
        <span className='tag tip' title={rest.join(', ')}>
          +{rest.length} more
        </span>
      ) : null}
    </span>
  )
}

/* ════════════════════ GOVERNANCE & RISK BANNER ════════════════════
   Numbers come server-computed from dashboard().risk / coverage / agreements.
   Tones: ok (green) / warn (amber) / risk (red) / info (blue). */
const TONE = {ok: '#2E7D32', warn: '#B45309', risk: '#C62828', info: '#1976D2', muted: '#6B7280'}

function GovTile({tile, active, onPick}) {
  const color = TONE[tile.tone] || TONE.muted
  const clickable = !!tile.filterable
  return (
    <Box
      component={clickable ? 'button' : 'div'}
      type={clickable ? 'button' : undefined}
      onClick={clickable ? () => onPick(tile.key) : undefined}
      title={tile.hint || tile.label}
      sx={{
        flex: '1 1 175px',
        minWidth: 160,
        textAlign: 'left',
        font: 'inherit',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.75,
        borderRadius: 3,
        border: 2,
        borderColor: active ? color : 'divider',
        bgcolor: active ? alpha(color, 0.05) : 'background.paper',
        cursor: clickable ? 'pointer' : 'default',
        transition: 'all .14s',
        ...(clickable ? {'&:hover': {borderColor: active ? color : alpha(color, 0.45), transform: 'translateY(-2px)', boxShadow: '0 8px 22px rgba(15,23,42,.08)'}} : {}),
      }}
    >
      <Box sx={{width: 40, height: 40, borderRadius: '12px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha(color, 0.12), color}}>
        {tile.icon}
      </Box>
      <Box sx={{minWidth: 0}}>
        <Typography sx={{fontWeight: 800, fontSize: 20, color, lineHeight: 1}}>{tile.value}</Typography>
        <Typography sx={{fontWeight: 700, fontSize: 12, color: 'text.primary', mt: 0.4}}>{tile.label}</Typography>
        <Typography sx={{fontSize: 11, color: 'text.secondary'}}>{tile.sub}</Typography>
      </Box>
    </Box>
  )
}

function GovBanner({dash, active, onPick}) {
  const cov = dash.coverage || {}
  const risk = dash.risk || {}
  const agr = dash.agreements || {}
  const pct = cov.pct != null ? cov.pct : 100
  const ungoverned = (cov.totalCodes || 0) - (cov.governedCodes || 0)
  const reviewDue = (risk.expired || 0) + (risk.expiringSoon || 0) + (risk.reviewDue || 0)
  const tiles = [
    {
      key: 'all',
      filterable: true,
      icon: <ShieldOutlinedIcon style={{fontSize: 22}} />,
      value: pct + '%',
      label: 'Agreement coverage',
      sub: (cov.governedCodes || 0) + '/' + (cov.totalCodes || 0) + ' codes governed',
      tone: pct >= 90 ? 'ok' : pct >= 70 ? 'warn' : 'risk',
      hint: 'Share of D2C codes operating under a signed (Stage 2) agreement. Click to clear filters.',
    },
    {
      key: 'ungoverned',
      filterable: true,
      icon: <WarningAmberIcon style={{fontSize: 22}} />,
      value: ungoverned,
      label: 'Ungoverned codes',
      sub: 'no signed agreement',
      tone: ungoverned ? 'risk' : 'ok',
      hint: 'Codes with no governing Stage-2 agreement in scope — commercial exposure. Click to filter partners.',
    },
    {
      key: 'reviewDue',
      icon: <AutorenewIcon style={{fontSize: 22}} />,
      value: reviewDue,
      label: 'Renewals / reviews due',
      sub: (risk.expired || 0) + ' expired · within 90d',
      tone: risk.expired ? 'risk' : reviewDue ? 'warn' : 'ok',
      hint: 'Agreements expired, expiring or due for review within 90 days (open-ended contracts use a standard review term).',
    },
    {
      key: 'agingS1',
      icon: <AccessTimeOutlinedIcon style={{fontSize: 22}} />,
      value: risk.stage1Aging || 0,
      label: 'Open Stage-1 aging',
      sub: '> ' + STAGE1_SLA_DAYS + 'd unsigned',
      tone: risk.stage1Aging ? 'warn' : 'ok',
      hint: 'Stage-1 (email-received) agreements unsigned beyond the ' + STAGE1_SLA_DAYS + '-day SLA — running on a handshake.',
    },
    {
      key: 'pendingApproval',
      icon: <VerifiedUserOutlinedIcon style={{fontSize: 22}} />,
      value: agr.pendingApproval || 0,
      label: 'Pending approval',
      sub: 'maker-checker queue',
      tone: agr.pendingApproval ? 'info' : 'ok',
      hint: 'Uploaded agreements awaiting commercial sign-off before they govern.',
    },
  ]
  if (risk.overlapping)
    tiles.push({
      key: 'overlap',
      icon: <BlockOutlinedIcon style={{fontSize: 22}} />,
      value: risk.overlapping,
      label: 'Overlapping agreements',
      sub: 'supersede one to resolve',
      tone: 'risk',
      hint: 'Two governing agreements of the same type cover the same codes in overlapping windows. This is allowed but ambiguous — open either agreement and use “Supersede” on the older one to resolve.',
    })
  return (
    <Box sx={{border: 1, borderColor: 'divider', borderRadius: 3, bgcolor: 'background.paper', boxShadow: '0 1px 2px rgba(15,23,42,.04)', p: 2.5, mb: 2}}>
      <Stack direction='row' alignItems='center' spacing={1} sx={{mb: 1.5}}>
        <ShieldOutlinedIcon sx={{fontSize: 16, color: 'text.secondary'}} />
        <Typography sx={{fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'text.secondary'}}>Governance &amp; Risk</Typography>
        {active !== 'all' ? (
          <Button size='small' onClick={() => onPick('all')} sx={{textTransform: 'none', fontWeight: 600, fontSize: 12, ml: 0.5}}>
            Clear filter
          </Button>
        ) : null}
      </Stack>
      <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1.5}}>
        {tiles.map((t) => (
          <GovTile key={t.key} tile={t} active={t.key !== 'all' && active === t.key} onPick={onPick} />
        ))}
      </Box>
    </Box>
  )
}

/* per-agreement risk chips (server-computed flags on the timeline item) */
function AgrRiskChips({item}) {
  const chips = riskChips(item, fmtDate)
  if (!chips.length) return null
  return (
    <span style={{display: 'inline-flex', flexWrap: 'wrap', gap: 4, marginTop: 4}}>
      {chips.map((c, i) =>
        c.blue ? (
          <span
            key={i}
            className='mini-pill'
            title={c.tip}
            style={{fontSize: 10.5, background: 'rgba(25,118,210,.12)', color: '#1976D2'}}
          >
            {c.text}
          </span>
        ) : (
          <span key={i} className={'mini-pill ' + c.cls} title={c.tip} style={{fontSize: 10.5}}>
            {c.text}
          </span>
        )
      )}
    </span>
  )
}

/* per-partner coverage cell — mini bar + governed/total + % */
function CoverageCell({governed, total}) {
  const pct = total ? Math.round((governed / total) * 100) : 100
  const color = pct >= 90 ? TONE.ok : pct >= 70 ? TONE.warn : TONE.risk
  return (
    <span style={{display: 'inline-flex', alignItems: 'center', gap: 8}}>
      <span className='pbar' style={{width: 54}}>
        <span style={{width: pct + '%', background: color}} />
      </span>
      <span style={{fontSize: 12, fontWeight: 700, color}}>
        {governed}/{total}
      </span>
    </span>
  )
}

/* ════════════════════ MAIN PAGE ════════════════════ */
export default function Agreements() {
  const navigate = useNavigate()
  const toast = useToast()
  const [params] = useSearchParams()

  const [fetchParents] = useLazyListParentsQuery()
  const [fetchDashboard] = useLazyDashboardQuery()
  const [fetchDownload] = useLazyDownloadAgreementQuery()
  const [approveAgreement] = useApproveAgreementMutation()
  const [rejectAgreement] = useRejectAgreementMutation()
  const [supersedeAgreement] = useSupersedeAgreementMutation()

  /* approver visibility — admins always; otherwise the AGREEMENT_APPROVER access.
     try/catch: token/store may be absent (tests) — degrade to read-only. */
  let ADMIN = false
  let access = []
  try {
    ADMIN = isAdmin()
    access = getAccess() || []
  } catch (e) {
    /* no auth context — read-only view */
  }
  const CAN_APPROVE = ADMIN || access.indexOf('AGREEMENT_APPROVER') >= 0

  /* ?p=<partnerId> — partner timeline pane ("View agreements" / post-upload pin) */
  const selPartnerId = params.get('p') || ''
  /* ?upload=<partnerId> — deep-link from the Partner 360 checklist: open the
     Upload Agreement drawer pre-set to that partner so the user can upload right away. */
  const uploadPartnerId = params.get('upload') || ''

  /* ── live data: parents + dashboard ── */
  const [parents, setParents] = useState([])
  const [dash, setDash] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadErr, setLoadErr] = useState('')
  const [version, setVersion] = useState(0) // bumped after every mutation → children refetch

  const loadAll = (silent) => {
    if (!silent) setLoading(true)
    setLoadErr('')
    Promise.all([fetchParents().unwrap(), fetchDashboard().unwrap()])
      .then(([pData, dData]) => {
        setParents(pData || [])
        setDash(dData || {})
        setLoading(false)
      })
      .catch((e) => {
        setLoadErr(apiError(e))
        setLoading(false)
      })
  }
  useEffect(() => {
    loadAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const refresh = () => {
    loadAll(true)
    setVersion((v) => v + 1)
  }

  /* filter state + risk quick-filter */
  const [filterStatus, setFilterStatus] = useState('all')
  const [filterParent, setFilterParent] = useState('all')
  const [searchQ, setSearchQ] = useState('')
  const [riskFilter, setRiskFilter] = useState('all') // all | ungoverned (row filters); other tiles are global counts
  const [justUploaded, setJustUploaded] = useState('') // partnerId whose row auto-expands after an upload
  const [promoteAgr, setPromoteAgr] = useState(null) // Stage-1 agreement being promoted (requires signed doc)
  const [supAgr, setSupAgr] = useState(null) // agreement being marked superseded (reason mandatory)
  const [rejectAgr, setRejectAgr] = useState(null) // PENDING_APPROVAL agreement being rejected

  /* upload drawer — {partnerId, codes:[courier _id]} (presetCodes for code-scoped entry) */
  const [uploadState, setUploadState] = useState(null)
  const [previewState, setPreviewState] = useState(null) // {agr}

  /* open the Upload Agreement drawer when arriving via ?upload=<partnerId>
     (Partner 360 checklist → "Add a signed (Stage 2) agreement"). */
  useEffect(() => {
    if (uploadPartnerId) setUploadState({partnerId: uploadPartnerId, codes: []})
  }, [uploadPartnerId])

  /* D2C parents (module scope) */
  const d2cParents = useMemo(
    () => parents.filter((p) => (p.verticals || []).some((v) => VISIBLE_VERTICALS.indexOf(v) >= 0)),
    [parents]
  )
  const selPartner = useMemo(
    () => parents.find((p) => p.id === selPartnerId) || null,
    [parents, selPartnerId]
  )

  /* parent rows for the table: parents list ⋈ dashboard.perParent rollups */
  const rows = useMemo(() => {
    const per = {}
    ;((dash && dash.perParent) || []).forEach((pp) => {
      per[pp.parentId] = pp
    })
    let r = d2cParents.map((p) => {
      const pp = per[p.id] || {}
      const signed = pp.agreementsSigned || 0
      const pending = pp.agreementsPendingSigning || 0
      return {
        id: p.id,
        name: p.name,
        color: colorFor(p.name),
        status: p.status,
        numCodes: pp.totalCodes || 0,
        totalAgrs: signed + pending,
        signed,
        pending,
        governed: pp.governedCodes || 0,
        partnerObj: p,
      }
    })
    if (filterStatus === 'active') r = r.filter((row) => row.signed > 0)
    else if (filterStatus === 'pending') r = r.filter((row) => row.pending > 0)
    if (filterParent !== 'all') r = r.filter((row) => row.id === filterParent)
    const qq = searchQ.trim().toLowerCase()
    if (qq) r = r.filter((row) => row.name.toLowerCase().indexOf(qq) >= 0)
    if (riskFilter === 'ungoverned') r = r.filter((row) => row.governed < row.numCodes)
    return r
  }, [d2cParents, dash, filterStatus, filterParent, searchQ, riskFilter])

  /* page-level KPIs (server-computed, not affected by filters) */
  const agrTotals = (dash && dash.agreements) || {}
  const cov = (dash && dash.coverage) || {}
  const covered = ((dash && dash.perParent) || []).filter(
    (pp) => (pp.agreementsSigned || 0) + (pp.agreementsPendingSigning || 0) > 0
  ).length
  const covPct = cov.pct != null ? cov.pct : 100

  /* ── actions (server-backed, shared by expand panel + preview) ── */
  const openUpload = (partnerId, presetCodes) =>
    setUploadState({partnerId: partnerId || '', codes: presetCodes || []})
  const openPreview = (a) => setPreviewState({agr: a})

  const downloadAgreement = async (a) => {
    try {
      const r = await fetchDownload({id: a.id}).unwrap()
      if (r && r.url) window.open(r.url, '_blank', 'noopener')
      else toast('No document available for download.', 'error')
    } catch (e) {
      toast(apiError(e), 'error')
    }
  }
  const approve = async (a) => {
    try {
      const res = await approveAgreement(a.id).unwrap()
      const n = (res && res.supersededIds ? res.supersededIds : []).length
      toast('Agreement approved — now Active & governing' + (n ? ' — superseded ' + n + ' prior.' : '.'), 'success')
      refresh()
    } catch (e) {
      toast(apiError(e), 'error')
    }
  }
  const confirmReject = async (reason) => {
    const a = rejectAgr
    setRejectAgr(null)
    if (!a) return
    try {
      await rejectAgreement({id: a.id, reason}).unwrap()
      toast('Agreement rejected. The maker can re-upload a corrected version.', 'success')
      refresh()
    } catch (e) {
      toast(apiError(e), 'error')
    }
  }
  const confirmSupersede = async (reason) => {
    const a = supAgr
    setSupAgr(null)
    if (!a) return
    try {
      await supersedeAgreement({id: a.id, reason}).unwrap()
      toast('"' + a.title + '" marked Superseded — retained for audit, no longer governing.', 'success')
      refresh()
    } catch (e) {
      toast(apiError(e), 'error')
    }
  }

  const agrActions = {
    isAdmin: ADMIN,
    canApprove: CAN_APPROVE,
    onPromote: setPromoteAgr,
    onApprove: approve,
    onReject: setRejectAgr,
    onSupersede: setSupAgr,
  }

  /* ── table columns ── */
  const columns = [
    {
      key: 'name',
      label: 'Partner',
      render: (r) => (
        <div className='row center gap8'>
          <span
            className='avatar'
            style={{background: r.color, width: 28, height: 28, fontSize: 11, borderRadius: 7, flexShrink: 0}}
          >
            {initials(r.name)}
          </span>
          <a
            className='lnk'
            href={'/supply/agreements?p=' + r.id}
            title='Open the full agreement timeline for this partner'
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              navigate('/supply/agreements?p=' + r.id)
            }}
            style={{fontSize: 13, fontWeight: 700}}
          >
            {r.name}
          </a>
        </div>
      ),
      value: (r) => r.name,
    },
    {
      key: 'numCodes',
      label: 'D2C Codes',
      align: 'center',
      render: (r) => <span style={{fontWeight: 700, fontSize: 13}}>{r.numCodes}</span>,
      value: (r) => r.numCodes,
    },
    {
      key: 'totalAgrs',
      label: 'Total Agreements',
      align: 'center',
      render: (r) => <span style={{fontWeight: 700, fontSize: 13}}>{r.totalAgrs}</span>,
      value: (r) => r.totalAgrs,
    },
    {
      key: 'signed',
      label: 'Signed',
      align: 'center',
      render: (r) =>
        r.signed ? (
          <span className='mini-pill mp-green'>{r.signed}</span>
        ) : (
          <span className='muted'>—</span>
        ),
      value: (r) => r.signed,
    },
    {
      key: 'pending',
      label: 'Pending',
      align: 'center',
      render: (r) =>
        r.pending ? (
          <span className='mini-pill mp-amber'>{r.pending}</span>
        ) : (
          <span className='muted'>—</span>
        ),
      value: (r) => r.pending,
    },
    {
      key: 'coverage',
      label: 'Coverage',
      align: 'center',
      render: (r) => <CoverageCell governed={r.governed} total={r.numCodes} />,
      value: (r) => (r.numCodes ? Math.round((r.governed / r.numCodes) * 100) : 100),
      exportValue: (r) => (r.numCodes ? Math.round((r.governed / r.numCodes) * 100) : 100) + '%',
    },
    {
      key: 'summary',
      label: 'Status Summary',
      render: (r) => <AgrParentSummary row={r} />,
      export: false,
      value: (r) => r.signed + ' signed / ' + r.pending + ' pending',
    },
  ]

  const rowActions = (r) => {
    const acts = [
      {
        label: 'View agreements',
        icon: <VisibilityOutlinedIcon style={{width: 17, height: 17}} />,
        onClick: () => navigate('/supply/agreements?p=' + r.id),
      },
      {
        label: 'Open Partner 360',
        icon: <StorefrontOutlinedIcon style={{width: 17, height: 17}} />,
        onClick: () => navigate('/supply/partners/' + r.id),
      },
    ]
    if (ADMIN) {
      acts.push({sep: true})
      acts.push({
        label: 'Upload Agreement',
        icon: <UploadIcon style={{width: 17, height: 17}} />,
        onClick: () => openUpload(r.id, []),
      })
    }
    return acts
  }

  return (
    <>
      <PageTitle>Courier Agreements</PageTitle>
      <SupplyHero
        icon={<DescriptionOutlinedIcon />}
        title='Agreements Overview'
        subtitle='D2C courier agreements — master agreements & addendums, organised by partner, code and stage.'
        actions={
          ADMIN ? (
            <Button variant='contained' disableElevation startIcon={<AddIcon />} onClick={() => openUpload(null, [])} sx={HERO_BTN_SOLID}>
              Upload Agreement
            </Button>
          ) : null
        }
      />

      {selPartnerId ? (
        /* ───────── PARTNER TIMELINE PANE (?p=) ───────── */
        <>
          <div style={{margin: '0 0 14px'}}>
            <button className='btn btn-sm' onClick={() => navigate('/supply/agreements')}>
              <ArrowBackIcon style={{width: 15, height: 15}} />
              Back to Agreements Overview
            </button>
          </div>
          <PartnerTimeline
            partnerId={selPartnerId}
            partner={selPartner}
            isAdmin={ADMIN}
            canApprove={CAN_APPROVE}
            template={AGR_TEMPLATE}
            version={version}
            onUpload={openUpload}
            onPreview={openPreview}
            onChanged={refresh}
            toast={toast}
          />
        </>
      ) : loading ? (
        <TableSkeleton rows={6} kpis={5} />
      ) : loadErr ? (
        <LoadErrorPanel message={loadErr} onRetry={() => loadAll()} title='Couldn’t load the agreements overview' />
      ) : (
        <>
          {/* ───────── GOVERNANCE & RISK BANNER (server-computed) ───────── */}
          <GovBanner
            dash={dash || {}}
            active={riskFilter}
            onPick={(k) => setRiskFilter((c) => (c === k ? 'all' : k))}
          />

          {/* ───────── KPI STRIP (global D2C totals) ───────── */}
          <KpiStrip
            cards={[
              {
                label: 'Total D2C Agreements',
                value: agrTotals.total || 0,
                icon: <DescriptionOutlinedIcon />,
                sub: 'MSAs + addendums',
              },
              {
                label: 'Signed',
                value: agrTotals.signed || 0,
                icon: <CheckCircleOutlineIcon />,
                sub: 'Stage 2 · governing',
                accent: '#2E7D32',
              },
              {
                label: 'Pending Signing',
                value: agrTotals.pendingSigning || 0,
                icon: <AccessTimeOutlinedIcon />,
                sub: 'Stage 1 · awaiting signature',
                accent: '#F57C00',
              },
              {
                label: 'Code Coverage',
                value: covPct + '%',
                icon: <ShieldOutlinedIcon />,
                sub: (cov.governedCodes || 0) + '/' + (cov.totalCodes || 0) + ' codes governed',
                accent: covPct >= 90 ? '#2E7D32' : covPct >= 70 ? '#B45309' : '#C62828',
              },
              {
                label: 'Partners Covered',
                value: covered,
                icon: <StorefrontOutlinedIcon />,
                sub: 'of ' + d2cParents.length + ' D2C partners',
              },
            ]}
          />

          {/* ───────── FILTER BAR ───────── */}
          <Box sx={{mt: 2, mb: 2, p: 2, borderRadius: 3, border: 1, borderColor: 'divider', bgcolor: 'background.paper', boxShadow: '0 1px 2px rgba(15,23,42,.04)'}}>
            <Stack direction='row' alignItems='center' spacing={0.75} sx={{mb: 1.25}}>
              <FilterAltOutlinedIcon sx={{fontSize: 16, color: 'text.secondary'}} />
              <Typography sx={{fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'text.secondary'}}>Filters</Typography>
            </Stack>
            <Box sx={{display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-end'}}>
            <Box sx={{minWidth: 180}}>
              <Typography sx={{fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'text.secondary', mb: 0.5}}>Status</Typography>
              <SearchSelect
                value={filterStatus}
                placeholder='All statuses'
                options={[
                  {value: 'all', label: 'All statuses'},
                  {value: 'active', label: 'Has signed agreements'},
                  {value: 'pending', label: 'Has pending signing'},
                ]}
                onChange={(v) => setFilterStatus(v || 'all')}
              />
            </Box>
            <Box sx={{minWidth: 200}}>
              <Typography sx={{fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'text.secondary', mb: 0.5}}>Partner</Typography>
              <SearchSelect
                value={filterParent}
                placeholder='All partners'
                options={[{value: 'all', label: 'All partners'}, ...d2cParents.map((p) => ({value: p.id, label: p.name}))]}
                onChange={(v) => setFilterParent(v || 'all')}
              />
            </Box>
            <Box sx={{flex: 1, minWidth: 220}}>
              <Typography sx={{fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'text.secondary', mb: 0.5}}>Search</Typography>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1, border: 1, borderColor: 'divider', borderRadius: 2, px: 1.5, height: 40, bgcolor: 'background.paper', '&:focus-within': {borderColor: 'primary.main'}}}>
                <SearchIcon style={{width: 17, height: 17, color: 'var(--neutral)'}} />
                <input
                  type='text'
                  id='agrOvSearch'
                  placeholder='Search partner…'
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                  style={{border: 'none', outline: 'none', width: '100%', fontSize: 13, background: 'transparent', fontFamily: 'inherit'}}
                />
              </Box>
            </Box>
            </Box>
          </Box>

          {/* ───────── EXPANDABLE PARENT → CODE → AGREEMENTS TABLE ───────── */}
          <SupplyTable
            columns={columns}
            rows={rows}
            rowKey={(r) => r.id}
            hideSearch
            pageSize={15}
            initialExpandedKey={justUploaded || undefined}
            emptyText='No D2C courier partners match your current filters.'
            exportName='agreements-overview'
            expand={(r) => (
              <ExpandPanel
                row={r}
                isAdmin={ADMIN}
                version={version}
                onUpload={openUpload}
                onView={openPreview}
                onDownload={downloadAgreement}
                actions={agrActions}
                navigate={navigate}
              />
            )}
            rowActions={rowActions}
          />
        </>
      )}

      {/* ───────── UPLOAD DRAWER (M2.4) — presetCodes = courier _ids ───────── */}
      <UploadDrawer
        open={!!uploadState}
        onClose={() => setUploadState(null)}
        presetPartnerId={uploadState ? uploadState.partnerId : ''}
        presetCodes={uploadState ? uploadState.codes : []}
        partners={d2cParents}
        toast={toast}
        onUploaded={(partnerId) => {
          /* Stay on the Overview and auto-expand the partner row so the new agreement
             (and its "Update to Stage 2" action) is visible right here. */
          setFilterStatus('all')
          setFilterParent('all')
          setSearchQ('')
          setRiskFilter('all')
          setJustUploaded(partnerId)
          refresh()
          if (selPartnerId && selPartnerId !== partnerId) navigate('/supply/agreements')
        }}
      />

      {/* ───────── PREVIEW MODAL ───────── */}
      <Modal
        open={!!previewState}
        onClose={() => setPreviewState(null)}
        title={previewState ? previewState.agr.title : ''}
        width={600}
        icon={<DescriptionOutlinedIcon fontSize='small' />}
        actions={[
          {label: 'Close', onClick: () => setPreviewState(null)},
          {
            label: 'Download',
            primary: true,
            icon: <DownloadOutlinedIcon style={{width: 16, height: 16}} />,
            closeOnClick: false,
            onClick: () => previewState && downloadAgreement(previewState.agr),
          },
        ]}
      >
        {previewState ? <PreviewBody agr={previewState.agr} /> : null}
      </Modal>

      {/* ───────── STAGE 1 → STAGE 2 (requires signed document — shared M2.5 drawer) ───────── */}
      <StageDrawer
        open={!!promoteAgr}
        agr={promoteAgr}
        template={AGR_TEMPLATE}
        toast={toast}
        onClose={() => setPromoteAgr(null)}
        onDone={() => {
          const pid = promoteAgr ? promoteAgr.parentId : ''
          if (pid) setJustUploaded(pid)
          refresh()
          setPromoteAgr(null)
        }}
      />

      {/* ───────── MARK SUPERSEDED (reason mandatory) ───────── */}
      <ConfirmDialog
        open={!!supAgr}
        onClose={() => setSupAgr(null)}
        title={supAgr ? 'Mark "' + supAgr.title + '" as superseded?' : ''}
        danger
        needReason
        okLabel='Mark Superseded'
        body='This agreement will be retained for audit but will no longer govern any courier code.'
        onOk={confirmSupersede}
      />

      {/* ───────── REJECT (maker-checker, reason mandatory) ───────── */}
      <ConfirmDialog
        open={!!rejectAgr}
        onClose={() => setRejectAgr(null)}
        title={rejectAgr ? 'Reject "' + rejectAgr.title + '"?' : ''}
        danger
        needReason
        okLabel='Reject'
        body='The agreement will be marked Rejected (terminal) and will never govern. The maker can upload a corrected version.'
        onOk={confirmReject}
      />
    </>
  )
}

/* ════════════════════ EXPAND PANEL ════════════════════
   Lazy-loads agreementTimeline(parentId) + codesByParent(parentId) on expand.
   Parent-wide agreements (appliesToAll) first, then per-code sections with
   status dot, agreement badge, deep-dive link and code-scoped upload. */
function ExpandPanel({row, isAdmin, version, onUpload, onView, onDownload, actions, navigate}) {
  const parentId = row.id
  const partnerName = row.name

  /* SUBSCRIBED reads — re-render automatically when an agreement mutation
     invalidates the Agreement cache, so Approve / Update Stage reflect the new
     state in-place without a manual refresh. */
  const {data: items = [], isLoading: tLoading, isError, error, refetch: refetchTimeline} = useAgreementTimelineQuery(
    parentId,
    {skip: !parentId}
  )
  const {data: codesRaw, isLoading: cLoading} = useCodesByParentQuery(parentId, {skip: !parentId})
  const codes = useMemo(() => (Array.isArray(codesRaw) ? codesRaw : (codesRaw && codesRaw.data) || []), [codesRaw])
  const loading = tLoading || cLoading
  const loadErr = isError ? apiError(error) : ''

  useEffect(() => {
    if (version) refetchTimeline()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version])

  if (loading)
    return (
      <Box sx={{p: 2.5, fontSize: 13, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1}}>
        <SyncIcon className='ps-spin' style={{width: 15, height: 15}} />
        Loading agreements for {partnerName}…
      </Box>
    )
  if (loadErr)
    return (
      <Box sx={{p: 2.5, fontSize: 13, color: 'error.main', display: 'flex', alignItems: 'center', gap: 1}}>
        <WarningAmberIcon style={{width: 16, height: 16}} />
        Could not load agreements: {loadErr}
      </Box>
    )

  const agrs = items.map((it) => it.agreement)

  if (!items.length) {
    return (
      <Box sx={{p: 2.5, fontSize: 13, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
        <DescriptionOutlinedIcon style={{width: 16, height: 16}} />
        No D2C agreements on record for {partnerName}.
        {isAdmin ? (
          <Button size='small' variant='contained' disableElevation startIcon={<AddIcon style={{width: 14, height: 14}} />} onClick={() => onUpload(parentId, [])} sx={{ml: 1, borderRadius: '8px', textTransform: 'none', fontWeight: 600}}>
            Upload
          </Button>
        ) : null}
      </Box>
    )
  }

  const parentWide = items.filter((it) => it.agreement.appliesToAll)

  const codeSecLabel = {fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.75}
  return (
    <Box sx={{p: {xs: 2, md: 2.5}, bgcolor: (th) => alpha(th.palette.text.primary, 0.02)}}>
      {/* Section 1: Parent-wide agreements */}
      <Box sx={{mb: 2.5}}>
        <Typography sx={{...codeSecLabel, mb: 1}}>
          <DescriptionOutlinedIcon sx={{fontSize: 15}} />
          Parent-wide agreements (all codes)
        </Typography>
        <MiniAgrTable items={parentWide} onView={onView} onDownload={onDownload} actions={actions} />
      </Box>

      {/* Section 2: Per-code sections */}
      {codes.length ? (
        <>
          <Typography sx={{...codeSecLabel, mb: 1.25}}>
            <SettingsInputComponentOutlinedIcon sx={{fontSize: 15}} />
            Per-code agreements
          </Typography>
          <Stack spacing={1.5} sx={{maxHeight: 460, overflow: 'auto', pr: 0.5}}>
            {codes.map((c) => {
              const codeItems = items.filter((it) => !it.agreement.appliesToAll && (it.agreement.linkedCpIds || []).indexOf(c.id) >= 0)
              const cActive = c.isActive !== false
              const badge = codeBadgeFor(c.id, agrs)
              return (
                <Box key={c.id} sx={{border: 1, borderColor: 'divider', borderRadius: 2.5, overflow: 'hidden', bgcolor: 'background.paper'}}>
                  {/* code header */}
                  <Stack direction='row' alignItems='center' spacing={1} flexWrap='wrap' useFlexGap sx={{px: 1.75, py: 1.25, borderBottom: 1, borderColor: 'divider', bgcolor: (th) => alpha(th.palette.text.primary, 0.015)}}>
                    <Box title={cActive ? 'Active' : 'Inactive'} sx={{width: 8, height: 8, borderRadius: '50%', flexShrink: 0, bgcolor: cActive ? 'success.main' : 'text.disabled'}} />
                    <Typography sx={{fontFamily: "'JetBrains Mono', monospace", fontSize: 12.5, fontWeight: 700, color: 'text.primary'}}>{codeLabel(c)}</Typography>
                    <Typography sx={{fontSize: 12, color: 'text.secondary'}}>{c.name}</Typography>
                    <Chip size='small' label='D2C' sx={{height: 18, fontSize: 10, fontWeight: 700, bgcolor: 'action.hover'}} />
                    <Stack direction='row' spacing={1} alignItems='center' sx={{ml: 'auto'}}>
                      <Badge status={badge.key} title={badge.tip} />
                      <Button size='small' variant='text' startIcon={<VisibilityOutlinedIcon style={{width: 14, height: 14}} />} onClick={() => navigate('/supply/codes/' + encodeURIComponent(codeLabel(c)))} sx={{textTransform: 'none', fontWeight: 600, fontSize: 12}}>
                        Deep dive
                      </Button>
                      {isAdmin ? (
                        <Button size='small' variant='outlined' startIcon={<AddIcon style={{width: 13, height: 13}} />} onClick={() => onUpload(parentId, [c.id])} sx={{borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: 11}}>
                          Add agreement
                        </Button>
                      ) : null}
                    </Stack>
                  </Stack>
                  <Box sx={{p: 1.5}}>
                    {codeItems.length ? (
                      <MiniAgrTable items={codeItems} onView={onView} onDownload={onDownload} actions={actions} />
                    ) : (
                      <Typography sx={{fontSize: 12, color: 'text.secondary'}}>No specific agreements linked to this code. Parent-wide agreements (above) may apply.</Typography>
                    )}
                  </Box>
                </Box>
              )
            })}
          </Stack>
        </>
      ) : null}

      {isAdmin ? (
        <Box sx={{mt: 1.5}}>
          <Button size='small' variant='contained' disableElevation startIcon={<AddIcon style={{width: 14, height: 14}} />} onClick={() => onUpload(parentId, [])} sx={{borderRadius: '10px', textTransform: 'none', fontWeight: 600}}>
            Upload Agreement for {partnerName}
          </Button>
        </Box>
      ) : null}
    </Box>
  )
}

/* mini agreement sub-table used in the expand panel — risk chips (server flags,
   under the title) and contextual governance actions (promote / approve / reject
   / supersede) in addition to View / Download. */
const MINI_STATUS = {
  PENDING_APPROVAL: {fg: '#1976D2', bg: 'rgba(25,118,210,.12)', label: 'Pending approval'},
  SUPERSEDED: {fg: '#475569', bg: '#F1F5F9', label: 'Superseded'},
  ACTIVE: {fg: '#166534', bg: '#DCFCE7', label: 'Active'},
}
function MiniAgrTable({items, onView, onDownload, actions}) {
  const A = actions || {}
  const statusPill = (a) => {
    const m = MINI_STATUS[a.status] || {fg: '#B91C1C', bg: '#FEE2E2', label: a.status}
    return <Chip size='small' label={m.label} sx={{height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: m.bg, color: m.fg}} />
  }
  const ActBtn = (key, onClick, icon, label, primary) => (
    <Button key={key} size='small' variant={primary ? 'contained' : 'outlined'} disableElevation onClick={onClick} startIcon={icon} sx={{borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: 11, py: 0.3, px: 1, minWidth: 0}}>
      {label}
    </Button>
  )
  if (!items.length) return <Typography sx={{fontSize: 12, color: 'text.secondary', py: 0.75}}>No agreements in this group.</Typography>
  return (
    <Stack spacing={1}>
      {items.map((it) => {
        const a = it.agreement
        const firstDoc = (a.documents || [])[0]
        const acts = [
          ActBtn('view', () => onView(a), <VisibilityOutlinedIcon style={{width: 13, height: 13}} />, 'View'),
          ActBtn('dl', () => onDownload(a), <DownloadOutlinedIcon style={{width: 13, height: 13}} />, 'Download'),
        ]
        if (a.status === 'PENDING_APPROVAL' && A.canApprove) {
          if (A.onApprove) acts.push(ActBtn('approve', () => A.onApprove(a), <VerifiedUserOutlinedIcon style={{width: 13, height: 13}} />, 'Approve', true))
          if (A.onReject) acts.push(ActBtn('reject', () => A.onReject(a), <CloseIcon style={{width: 13, height: 13}} />, 'Reject'))
        } else if (A.isAdmin) {
          if (isOpenStage1(a) && A.onPromote) acts.push(ActBtn('promote', () => A.onPromote(a), <CheckCircleOutlineIcon style={{width: 13, height: 13}} />, 'Update to Stage 2', true))
          else if (isSignedGoverning(a) && A.onSupersede) acts.push(ActBtn('supersede', () => A.onSupersede(a), <HistoryIcon style={{width: 13, height: 13}} />, 'Supersede'))
        }
        return (
          <Box key={a.id} sx={{p: 1.5, borderRadius: 2, border: 1, borderColor: 'divider', bgcolor: 'background.paper'}}>
            <Stack direction={{xs: 'column', md: 'row'}} spacing={1.5} justifyContent='space-between' alignItems={{md: 'center'}}>
              <Box sx={{minWidth: 0, flex: 1}}>
                <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap>
                  <Typography sx={{fontWeight: 700, fontSize: 12.5, color: 'text.primary'}}>{agrTypeShort(a)}</Typography>
                  <AgrStageBadge agr={a} />
                  {statusPill(a)}
                  {/* Feedback item 9: flag agreements/CPs with no document uploaded. */}
                  {!firstDoc && (
                    <Chip size='small' color='warning' variant='outlined' label='Not uploaded'
                      sx={{height: 20, fontSize: 10.5, fontWeight: 600}} />
                  )}
                </Stack>
                <Typography sx={{fontSize: 13, color: 'text.primary', mt: 0.5}} noWrap title={a.title}>{a.title}</Typography>
                <AgrRiskChips item={it} />
                <Typography sx={{fontSize: 11.5, color: 'text.secondary', mt: 0.25}}>
                  {fmtDate(a.effectiveFrom)}{a.effectiveTo ? ' → ' + fmtDate(a.effectiveTo) : ' · open'}{firstDoc ? ' · uploaded by ' + firstDoc.uploadedBy : ''}
                </Typography>
              </Box>
              <Stack direction='row' spacing={0.5} flexWrap='wrap' useFlexGap sx={{flexShrink: 0}}>{acts}</Stack>
            </Stack>
          </Box>
        )
      })}
    </Stack>
  )
}

/* ════════════════════ PREVIEW MODAL BODY ════════════════════ */
function PreviewBody({agr: a}) {
  const docs = a.documents || []
  return (
    <>
      <dl className='dl' style={{gridTemplateColumns: 'auto 1fr', maxWidth: 560, marginBottom: 14}}>
        <dt>Type</dt>
        <dd>{agrTypeFull(a)}</dd>
        <dt>Stage</dt>
        <dd>
          {a.stage === 'SIGNED' ? (
            <Badge status='signed' label='Stage 2: Signed' />
          ) : (
            <Badge status='draft' label='Stage 1: Email Received' />
          )}
        </dd>
        <dt>Status</dt>
        <dd>{a.status}</dd>
        <dt>Vertical</dt>
        <dd>
          <AgrVertChips verticals={a.verticals} />
        </dd>
        <dt>Effective</dt>
        <dd>
          {fmtDate(a.effectiveFrom)}
          {a.effectiveTo ? ' → ' + fmtDate(a.effectiveTo) : ' · open-ended'}
        </dd>
        <dt>Applies to</dt>
        <dd>
          {a.appliesToAll ? (
            'All ' + (a.parentName || '') + ' codes'
          ) : (
            <CodeChips codes={a.linkedCpAccountCodes} />
          )}
        </dd>
        {a.signedBy ? (
          <>
            <dt>Signed</dt>
            <dd>
              {a.signedBy}
              {a.signedAt ? ' · ' + fmtDateTime(a.signedAt) : ''}
            </dd>
          </>
        ) : null}
        {a.notes ? (
          <>
            <dt>Notes</dt>
            <dd>{a.notes}</dd>
          </>
        ) : null}
      </dl>
      {docs.length ? (
        <div style={{marginBottom: 6}}>
          <div className='seclabel' style={{marginBottom: 6, color: 'var(--ink-2)'}}>
            Documents on file
          </div>
          {docs.map((d, i) => (
            <div key={i} className='tl-meta' style={{display: 'flex', alignItems: 'center', gap: 6}}>
              <DescriptionOutlinedIcon style={{width: 14, height: 14}} />
              <span className='mono' style={{fontSize: 12}}>
                {d.fileName}
              </span>
              <span className='muted' style={{fontSize: 11.5}}>
                Stage {d.stage === 'SIGNED' ? 2 : 1} · {d.uploadedBy} · {fmtDateTime(d.uploadedAt)}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div style={{marginBottom: 6}}>
          <Chip size='small' color='warning' variant='outlined' label='Not uploaded'
            sx={{height: 20, fontSize: 10.5, fontWeight: 600}} />
          <span className='muted' style={{fontSize: 11.5, marginLeft: 8}}>No agreement document on file.</span>
        </div>
      )}
      <div className='warnbox info'>
        <InfoOutlinedIcon style={{width: 16, height: 16}} />
        <div>Use Download to open the stored document (presigned S3 link, short-lived).</div>
      </div>
    </>
  )
}

/* ════════════════════ UPLOAD DRAWER (M2.4) ════════════════════ */
/* presetCodes: courier _id strings — when non-empty, pre-selects "Specific codes"
   + pre-ticks those codes. Vertical is fixed to D2C (module scope). */
const initForm = (presetPartnerId, presetCodes) => {
  const hasCodes = Array.isArray(presetCodes) && presetCodes.length > 0
  return {
    partnerId: presetPartnerId || '',
    vertical: 'D2C',
    type: '',
    addendumNo: '',
    title: '',
    stage: '',
    effFrom: '',
    effTo: '',
    appliesAll: hasCodes ? 'specific' : 'all',
    codes: hasCodes ? presetCodes.slice() : [], // courier _id values
    notes: '',
    file: null,
    routeForApproval: false, // maker-checker is opt-in; default files the agreement live
  }
}

function UploadDrawer({open, onClose, presetPartnerId, presetCodes, partners, toast, onUploaded}) {
  const [F, setF] = useState(initForm(presetPartnerId, presetCodes))
  const [errs, setErrs] = useState({})
  const [busy, setBusy] = useState(false)
  const [codeQuery, setCodeQuery] = useState('')
  const [partnerCodes, setPartnerCodes] = useState([]) // courier docs for the selected partner
  const [codesLoading, setCodesLoading] = useState(false)
  const [fetchCodesByParent] = useLazyCodesByParentQuery()
  const [uploadAgreement] = useUploadAgreementMutation()

  // reset whenever the drawer is (re)opened — pick up latest presets
  React.useEffect(() => {
    if (open) {
      setF(initForm(presetPartnerId, presetCodes))
      setErrs({})
      setBusy(false)
      setCodeQuery('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // load the partner's codes for the specific-scope multiselect
  React.useEffect(() => {
    if (!open || !F.partnerId) {
      setPartnerCodes([])
      return
    }
    let alive = true
    setCodesLoading(true)
    fetchCodesByParent(F.partnerId)
      .unwrap()
      .then((d) => {
        if (!alive) return
        setPartnerCodes(Array.isArray(d) ? d : (d && d.data) || [])
        setCodesLoading(false)
      })
      .catch((e) => {
        if (!alive) return
        setPartnerCodes([])
        setCodesLoading(false)
        toast(apiError(e), 'error')
      })
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, F.partnerId])

  const set = (patch) => setF((prev) => ({...prev, ...patch}))
  const clearErr = (key) =>
    setErrs((prev) => {
      if (!prev[key]) return prev
      const n = {...prev}
      delete n[key]
      return n
    })

  const isAddendum = F.type === 'Commercial Addendum' || F.type === 'Operational Addendum'
  const now = new Date()
  const backdated = !!F.effFrom && new Date(F.effFrom) < now

  const filteredCodes = partnerCodes.filter((c) => {
    const q = codeQuery.trim().toLowerCase()
    return (
      !q ||
      codeLabel(c).toLowerCase().indexOf(q) >= 0 ||
      String(c.name || '').toLowerCase().indexOf(q) >= 0
    )
  })

  const submit = async () => {
    const e = {}
    if (!F.partnerId) e.partner = true
    if (!F.vertical) e.vertical = true
    if (!F.type) e.type = true
    if (!F.title || F.title.trim().length < 5) e.title = true
    if (!F.stage) e.stage = true
    if (!F.effFrom) e.effFrom = true
    if (F.effFrom) {
      /* effective-from cannot be in the past by more than 365 days */
      const ef = new Date(F.effFrom)
      const floor = new Date(Date.now() - 365 * 86400000)
      if (ef < floor) e.effFrom = true
    }
    if (F.effTo && F.effFrom && new Date(F.effTo) <= new Date(F.effFrom)) e.effTo = true
    if (F.appliesAll === 'specific' && !F.codes.length) e.codes = true
    if (!F.file) e.file = true
    setErrs(e)
    if (Object.keys(e).length) {
      toast('Please fix the highlighted fields before uploading.', 'error')
      return
    }

    const request = {
      parentId: F.partnerId,
      verticals: ['D2C'],
      type: AGREEMENT_TYPE_ENUM[F.type] || F.type,
      addendumNo: isAddendum && String(F.addendumNo).trim() ? parseInt(F.addendumNo, 10) : null,
      title: F.title.trim(),
      stage: STAGE_ENUM[+F.stage], // '1' → EMAIL_RECEIVED, '2' → SIGNED
      effectiveFrom: F.effFrom,
      effectiveTo: F.effTo || null,
      appliesToAll: F.appliesAll === 'all',
      linkedCpIds: F.appliesAll === 'specific' ? F.codes.slice() : [],
      notes: F.notes ? F.notes.trim().slice(0, 500) : '',
      routeForApproval: F.routeForApproval,
    }

    setBusy(true)
    try {
      const res = await uploadAgreement({request, file: F.file}).unwrap()
      const n = (res && res.supersededIds ? res.supersededIds : []).length
      const isStage1 = +F.stage === 1
      toast(
        (F.routeForApproval
          ? 'Agreement uploaded and submitted for approval (maker-checker). It governs once approved.'
          : isStage1
          ? 'Filed as Stage 1 (Email received). Use "Update to Stage 2" when the signed copy arrives.'
          : 'Filed as Stage 2 (Signed) — now governing its codes.') +
          (n ? ' Superseded ' + n + ' prior agreement(s).' : ''),
        'success'
      )
      onUploaded(F.partnerId)
      onClose()
    } catch (err) {
      toast(apiError(err), 'error')
      setBusy(false)
    }
  }

  return (
    <SideDrawer
      open={open}
      onClose={onClose}
      title='Upload Agreement'
      width={560}
      footer={
        <>
          <button className='btn btn-ghost' onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className='btn btn-primary' disabled={busy} onClick={submit}>
            {busy ? (
              <SyncIcon className='ps-spin' style={{width: 16, height: 16}} />
            ) : (
              <UploadIcon style={{width: 16, height: 16}} />
            )}
            {busy ? 'Uploading…' : 'Upload Agreement'}
          </button>
        </>
      }
    >
      {busy ? (
        <div className='warnbox info' style={{marginBottom: 14}}>
          <UploadIcon style={{width: 16, height: 16}} />
          <div style={{flex: 1, display: 'flex', alignItems: 'center', gap: 8}}>
            <SyncIcon className='ps-spin' style={{width: 14, height: 14}} />
            Uploading <b>{F.file ? F.file.name : ''}</b> to the document vault…
          </div>
        </div>
      ) : null}

      {/* Backdated effective date (billing-impact flag) */}
      {backdated ? (
        <div className='warnbox' style={{marginBottom: 14}}>
          <WarningAmberIcon style={{width: 16, height: 16}} />
          <div>
            <b>Backdated effective date.</b> Terms apply before today — invoices already billed in this window
            may need re-rating / credit notes.
          </div>
        </div>
      ) : null}

      {/* Partner */}
      <Field
        label='Partner'
        required
        invalid={errs.partner}
        help='Select the parent courier partner. Must exist in the Partner Directory.'
        msg='Select the courier partner.'
      >
        <SearchSelect
          value={F.partnerId}
          invalid={errs.partner}
          placeholder='Search a partner…'
          options={partners.map((p) => ({value: p.id, label: p.name}))}
          onChange={(v) => {
            set({partnerId: v, codes: []})
            clearErr('partner')
          }}
        />
      </Field>

      {/* Freight vertical — module scope limits the options to D2C Freight */}
      <Field
        label='Freight vertical'
        required
        invalid={errs.vertical}
        help='Which vertical does this agreement govern? You can only file for verticals you have commercial access to.'
        msg='Select the freight vertical.'
      >
        <SearchSelect
          value={F.vertical}
          invalid={errs.vertical}
          placeholder='Select…'
          options={[{value: 'D2C', label: 'D2C Freight'}]}
          onChange={(v) => {
            set({vertical: v})
            clearErr('vertical')
          }}
        />
      </Field>

      {/* Agreement type */}
      <Field
        label='Agreement type'
        required
        invalid={errs.type}
        help='MSA = master contract (one per partner). Addendum = a rate or operational revision.'
        msg='Select the agreement type.'
      >
        <SearchSelect
          value={F.type}
          invalid={errs.type}
          placeholder='Select…'
          options={['Master Agreement (MSA)', 'Commercial Addendum', 'Operational Addendum']}
          onChange={(v) => {
            set({type: v})
            clearErr('type')
          }}
        />
      </Field>

      {/* Addendum number (addendums only — blank = server auto-assigns next) */}
      {isAddendum ? (
        <Field
          label='Addendum number'
          optional
          invalid={errs.addendumNo}
          help='Sequential number for this addendum under this partner. Leave blank — the system assigns the next number.'
          msg='Enter a valid addendum number.'
        >
          <input
            className='input'
            type='number'
            min='1'
            placeholder='auto'
            value={F.addendumNo}
            onChange={(e) => {
              set({addendumNo: e.target.value})
              clearErr('addendumNo')
            }}
          />
        </Field>
      ) : null}

      {/* Document title */}
      <Field
        label='Document title'
        required
        invalid={errs.title}
        help='Clear, descriptive title. Minimum 5 characters.'
        msg='Title must be at least 5 characters.'
      >
        <input
          className='input'
          placeholder='e.g. Delhivery Rate Revision Q1 FY27 — ENT Surface Codes'
          value={F.title}
          onChange={(e) => {
            set({title: e.target.value})
            clearErr('title')
          }}
        />
      </Field>

      {/* Stage */}
      <Field
        label='Stage'
        required
        invalid={errs.stage}
        help='Stage 1 = you have the formal approval email. Stage 2 = the signed document is received.'
        msg='Select the stage.'
      >
        <SearchSelect
          value={F.stage}
          invalid={errs.stage}
          placeholder='Select…'
          options={[
            {value: '1', label: 'Stage 1: Email Received'},
            {value: '2', label: 'Stage 2: Agreement Signed'},
          ]}
          onChange={(v) => {
            set({stage: v})
            clearErr('stage')
          }}
        />
      </Field>

      {/* Effective dates */}
      <div className='grid' style={{gridTemplateColumns: '1fr 1fr'}}>
        <Field
          label='Effective from'
          required
          invalid={errs.effFrom}
          help='Date the terms apply — NOT the date you received it.'
          msg='Effective-from date is required (max 365 days in the past).'
        >
          <input
            className='input'
            type='date'
            value={F.effFrom}
            onChange={(e) => {
              set({effFrom: e.target.value})
              clearErr('effFrom')
            }}
          />
        </Field>
        <Field
          label='Effective to'
          optional
          invalid={errs.effTo}
          help='Leave blank for open-ended (most common).'
          msg='Effective-to must be after effective-from.'
        >
          <input
            className='input'
            type='date'
            value={F.effTo}
            onChange={(e) => {
              set({effTo: e.target.value})
              clearErr('effTo')
            }}
          />
        </Field>
      </div>

      {/* Applies to */}
      <div className={'field' + (errs.appliesAll ? ' invalid' : '')}>
        <label>
          Applies to <span className='req'>*</span>
        </label>
        <label className='checkrow'>
          <input
            type='radio'
            name='uApplies'
            value='all'
            checked={F.appliesAll === 'all'}
            onChange={() => set({appliesAll: 'all', codes: []})}
          />
          All courier codes of this partner
        </label>
        <label className='checkrow'>
          <input
            type='radio'
            name='uApplies'
            value='specific'
            checked={F.appliesAll === 'specific'}
            onChange={() => set({appliesAll: 'specific'})}
          />
          Specific courier codes only
        </label>
        <div className='help'>
          <InfoOutlinedIcon style={{width: 13, height: 13}} />
          Specific-code agreements only affect the badge of the codes you list.
        </div>
      </div>

      {/* Specific codes multiselect (courier _id values) */}
      {F.appliesAll === 'specific' ? (
        !F.partnerId ? (
          <div className='warnbox' style={{marginBottom: 18}}>
            <WarningAmberIcon style={{width: 16, height: 16}} />
            <div>Select a partner first to choose specific codes.</div>
          </div>
        ) : (
          <div className={'field' + (errs.codes ? ' invalid' : '')}>
            <label>
              Specific courier codes <span className='req'>*</span>
            </label>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                border: '1px solid var(--border)',
                borderRadius: 6,
                padding: '0 10px',
                height: 38,
                background: '#fff',
                marginBottom: 8,
              }}
            >
              <span style={{display: 'flex', alignItems: 'center'}}>
                <SearchIcon style={{width: 16, height: 16, color: 'var(--neutral)'}} />
              </span>
              <input
                type='text'
                placeholder='Search codes...'
                value={codeQuery}
                onChange={(e) => setCodeQuery(e.target.value)}
                style={{
                  border: 'none',
                  outline: 'none',
                  width: '100%',
                  fontSize: 13,
                  background: 'transparent',
                  fontFamily: 'inherit',
                }}
              />
            </div>
            <div
              style={{
                border: '1px solid var(--border)',
                borderRadius: 6,
                maxHeight: 180,
                overflow: 'auto',
                padding: 6,
              }}
            >
              {codesLoading ? (
                <div style={{padding: 8, fontSize: 12, color: 'var(--neutral)', display: 'flex', alignItems: 'center', gap: 6}}>
                  <SyncIcon className='ps-spin' style={{width: 13, height: 13}} />
                  Loading codes…
                </div>
              ) : !filteredCodes.length ? (
                <div style={{padding: 8, fontSize: 12, color: 'var(--neutral)'}}>
                  No codes match.
                </div>
              ) : (
                filteredCodes.map((c) => (
                  <label key={c.id} className='checkrow' style={{padding: '5px 6px'}}>
                    <input
                      type='checkbox'
                      checked={F.codes.indexOf(c.id) >= 0}
                      onChange={(e) => {
                        setF((prev) => {
                          const next = prev.codes.slice()
                          const i = next.indexOf(c.id)
                          if (e.target.checked && i < 0) next.push(c.id)
                          if (!e.target.checked && i >= 0) next.splice(i, 1)
                          return {...prev, codes: next}
                        })
                        clearErr('codes')
                      }}
                    />
                    <span className='mono' style={{fontSize: 11}}>
                      {codeLabel(c)}
                    </span>
                    <span className='chip chip-d2c' style={{marginLeft: 'auto'}}>
                      D2C
                    </span>
                  </label>
                ))
              )}
            </div>
            <div className='help'>
              <InfoOutlinedIcon style={{width: 13, height: 13}} />
              <span>{F.codes.length} selected. At least one code is required.</span>
            </div>
            <div className='vmsg'>Select at least one courier code.</div>
          </div>
        )
      ) : null}

      {/* Agreement document */}
      <div className={'field' + (errs.file ? ' invalid' : '')}>
        <label>
          Agreement document <span className='req'>*</span>
        </label>
        <FileDrop
          accept='.pdf,.docx,.png,.jpg,.jpeg,.eml,.msg'
          maxMB={10}
          title='Drag & drop the agreement here'
          hint='PDF, DOCX, PNG, JPG, EML, MSG · max 10 MB · stored in Prozo document vault (S3)'
          sampleName='agreement-upload-template.txt'
          sampleLabel='Download template'
          sampleContent={AGR_TEMPLATE}
          onFile={(f) => {
            set({file: f})
            clearErr('file')
          }}
          onError={(msg) => {
            setErrs((prev) => ({...prev, file: true}))
            toast(msg, 'error')
          }}
        />
        <div className='vmsg'>Upload the agreement file to continue.</div>
      </div>

      {/* Internal notes */}
      <Field
        label='Internal notes'
        optional
        help='Any internal context. Max 500 characters.'
        msg=''
      >
        <textarea
          className='input'
          rows={2}
          maxLength={500}
          placeholder='e.g. Rahul from Delhivery sent this via WhatsApp; following up for the formal email.'
          value={F.notes}
          onChange={(e) => set({notes: e.target.value})}
        />
        <div className='help' style={{textAlign: 'right', justifyContent: 'flex-end'}}>
          <span>{F.notes.length}/500</span>
        </div>
      </Field>

      {/* Maker-checker — opt-in */}
      <div className='field' style={{marginBottom: 0}}>
        <label className='checkrow' style={{alignItems: 'flex-start'}}>
          <input
            type='checkbox'
            checked={F.routeForApproval}
            onChange={(e) => set({routeForApproval: e.target.checked})}
            style={{marginTop: 3}}
          />
          <span>
            <b>Route for approval before it governs</b> (maker-checker)
            <div className='muted' style={{fontSize: 12}}>
              Holds the agreement in a <b>Pending approval</b> queue — a commercial approver must Approve it
              before it counts as signed &amp; governing. Leave unticked to file it live.
            </div>
          </span>
        </label>
      </div>
    </SideDrawer>
  )
}

/* small form-field wrapper reproducing the mockup `.field` markup */
function Field({label, required, optional, invalid, help, msg, children}) {
  return (
    <div className={'field' + (invalid ? ' invalid' : '')}>
      <label>
        {label}
        {optional ? (
          <span className='opt'> (optional)</span>
        ) : required ? (
          <span className='req'> *</span>
        ) : null}
      </label>
      {children}
      {help ? (
        <div className='help'>
          <InfoOutlinedIcon style={{width: 13, height: 13}} />
          {help}
        </div>
      ) : null}
      <div className='vmsg'>{msg || 'Required.'}</div>
    </div>
  )
}

/* ════════════════════ PropTypes ════════════════════ */
AgrStageBadge.propTypes = {
  agr: PropTypes.object.isRequired,
}
AgrParentSummary.propTypes = {
  row: PropTypes.object.isRequired,
}
AgrVertChips.propTypes = {
  verticals: PropTypes.array,
}
CodeChips.propTypes = {
  codes: PropTypes.array,
}
ExpandPanel.propTypes = {
  row: PropTypes.object.isRequired,
  isAdmin: PropTypes.bool,
  version: PropTypes.number,
  onUpload: PropTypes.func,
  onView: PropTypes.func,
  onDownload: PropTypes.func,
  actions: PropTypes.object,
  navigate: PropTypes.func,
}
MiniAgrTable.propTypes = {
  items: PropTypes.array.isRequired,
  onView: PropTypes.func,
  onDownload: PropTypes.func,
  actions: PropTypes.object,
}
GovTile.propTypes = {
  tile: PropTypes.object.isRequired,
  active: PropTypes.bool,
  onPick: PropTypes.func,
}
GovBanner.propTypes = {
  dash: PropTypes.object.isRequired,
  active: PropTypes.string,
  onPick: PropTypes.func,
}
AgrRiskChips.propTypes = {
  item: PropTypes.object.isRequired,
}
CoverageCell.propTypes = {
  governed: PropTypes.number,
  total: PropTypes.number,
}
PreviewBody.propTypes = {
  agr: PropTypes.object.isRequired,
}
UploadDrawer.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  presetPartnerId: PropTypes.string,
  presetCodes: PropTypes.arrayOf(PropTypes.string),
  partners: PropTypes.array,
  toast: PropTypes.func,
  onUploaded: PropTypes.func,
}
Field.propTypes = {
  label: PropTypes.node,
  required: PropTypes.bool,
  optional: PropTypes.bool,
  invalid: PropTypes.bool,
  help: PropTypes.node,
  msg: PropTypes.node,
  children: PropTypes.node,
}
